import React from "react";
import { useEffect } from "react";
import { useForm } from "@tanstack/react-form";

import { Button, Select, TextField, TimePeriodSelect } from "../../../ui/index.ts";
import { timePeriodToDays } from "../../../ui/time-period-select/time-period-utils.ts";
import { employeeTypeOptions } from "../../../../../options/employeeTypeOptions.js";
import { formatFormFieldErrors } from "../../auth/forms/form-errors.ts";
import { useHrSetupApprove } from "../hooks/use-hr-setup-approve.ts";
import { useHrSetupLookups } from "../hooks/use-hr-setup-lookups.ts";
import {
  buildHrApproveQueryParams,
  hrApproveFormSchema,
  type HrApproveFormValues,
} from "../schemas/hr-approve.schema.ts";
import type { HrSetupEmployeeRow } from "../schemas/hr-setup-employee.schema.ts";
import { computeConfirmationDate } from "../utils/compute-confirmation-date.ts";
import { formatOnboardEmployeeName } from "../utils/format-onboard-employee-name.ts";
import { ApiError } from "../../../../utils/api.ts";
import { showError, showSuccess } from "../../../../utils/toast.ts";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";

import styles from "./hr-approve-tab.module.css";

export type HrApproveTabProps = {
  employee: HrSetupEmployeeRow;
  usesUsIdentityFields: boolean;
  onCancel: () => void;
  onSuccess: () => void;
};

const field = (name: keyof HrApproveFormValues) => name;

const EMPLOYEE_SUB_TYPE_OPTIONS = [
  { value: "Fulltime", label: "Full Time" },
  { value: "Intern", label: "Intern" },
];

const IS_TECH_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const PROVISION_PRESET_OPTIONS = [
  { value: "30", label: "1 Month" },
  { value: "90", label: "3 Months" },
  { value: "custom", label: "Custom" },
];

const NOTICE_PERIOD_PRESET_OPTIONS = [
  { value: "30", label: "1 Month" },
  { value: "60", label: "2 Months" },
  { value: "90", label: "3 Months" },
  { value: "custom", label: "Custom" },
];

const defaultValues: HrApproveFormValues = {
  departmentId: "",
  designationId: "",
  managerId: "",
  companyName: "",
  employeeType: "",
  leaveManagerId: "",
  employeeSubType: "",
  isTech: false,
  techStack: "",
  noticePeriodPreset: "30",
  customNoticePeriod: { amount: 1, unit: "month" },
  provisionPreset: "30",
  customProvision: { amount: 1, unit: "month" },
};

function computeNoticePeriodDays(values: HrApproveFormValues): number {
  if (values.noticePeriodPreset !== "custom") {
    return Number(values.noticePeriodPreset);
  }

  const custom = values.customNoticePeriod ?? { amount: 1, unit: "month" };
  return timePeriodToDays(custom.amount, custom.unit);
}

export function HrApproveTab({
  employee,
  onCancel,
  onSuccess,
}: HrApproveTabProps) {
  const isSqe = useSqeLabs();
  const { approveEmployee, loading: approveLoading } = useHrSetupApprove();
  const {
    departments,
    managers,
    companies,
    techStacks,
    designations,
    designationsLoading,
    error: lookupsError,
    fetchDesignations,
  } = useHrSetupLookups();

  const employeeName = formatOnboardEmployeeName(employee);
  const ccEmail = typeof employee.ccEmail === "string" ? employee.ccEmail : "";
 
  useEffect(() => {
    if (lookupsError != null && lookupsError !== "") {
      showError(lookupsError);
    }
  }, [lookupsError]);

  const form = useForm({
    defaultValues: isSqe ? { ...defaultValues, companyName: "SQE", employeeType: "SQELABS" } : defaultValues,
    validators: { onSubmit: hrApproveFormSchema as never },
    onSubmit: async ({ value }) => {
      try {
        const confirmationDate = computeConfirmationDate(
          value.provisionPreset,
          value.customProvision,
        );
        const formValues = value as HrApproveFormValues;
        const params = buildHrApproveQueryParams(
          formValues,
          confirmationDate,
          computeNoticePeriodDays(formValues),
        );
        await approveEmployee(employee.id, params);
        showSuccess("Employee approved successfully.");
        onSuccess();
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : "Failed to approve employee.";
        showError(msg);
      }
    },
  });

  const busy = approveLoading;

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      noValidate
    >
      <div className={styles.readOnlyRow}>
        <TextField
          id="hr-setup-approve-employee-name"
          label="Employee Name"
          value={employeeName}
          readOnly
          disabled
        />
        <TextField
          id="hr-setup-approve-cc-email"
          label="CC Email"
          value={ccEmail}
          readOnly
          disabled
        />
      </div>

      <div className={styles.grid}>
        <form.Field name={field("departmentId")}>
          {(f) => (
            <Select
              id="hr-setup-approve-department"
              label="Department"
              required
              searchable
              value={f.state.value}
              onChange={(v) => {
                f.handleChange(v);
                form.setFieldValue("designationId", "");
                void fetchDesignations(v);
              }}
              options={departments}
              placeholder="Select Department"
              disabled={busy}
              error={formatFormFieldErrors(f.state.meta.errors)}
            />
          )}
        </form.Field>

        <form.Field name={field("designationId")}>
          {(f) => (
            <Select
              id="hr-setup-approve-designation"
              label="Designation"
              required
              searchable
              value={f.state.value}
              onChange={(v) => f.handleChange(v)}
              options={designations}
              placeholder={designationsLoading ? "Loading…" : "Select Designation"}
              disabled={busy || designationsLoading}
              error={formatFormFieldErrors(f.state.meta.errors)}
            />
          )}
        </form.Field>

        <form.Field name={field("managerId")}>
          {(f) => (
            <Select
              id="hr-setup-approve-manager"
              label="Reporting Manager"
              required
              searchable
              value={f.state.value}
              onChange={(v) => f.handleChange(v)}
              options={managers}
              placeholder="Select Reporting Manager"
              disabled={busy}
              error={formatFormFieldErrors(f.state.meta.errors)}
            />
          )}
        </form.Field>

        <form.Field name={field("leaveManagerId")}>
          {(f) => (
            <Select
              id="hr-setup-approve-leave-manager"
              label="Leave Manager"
              required
              searchable
              value={f.state.value}
              onChange={(v) => f.handleChange(v)}
              options={managers}
              placeholder="Select Leave Manager"
              disabled={busy}
              error={formatFormFieldErrors(f.state.meta.errors)}
            />
          )}
        </form.Field>

        {!isSqe && <form.Field name={field("companyName")}>
          {(f) => (
            <Select
              id="hr-setup-approve-company-name"
              label="Company Name"
              required
              value={f.state.value}
              onChange={(v) => f.handleChange(v)}
              options={companies}
              placeholder="Select Company Name"
              disabled={busy}
              error={formatFormFieldErrors(f.state.meta.errors)}
            />
          )}
        </form.Field>}

        {!isSqe && <form.Field name={field("employeeType")}>
          {(f) => (
            <Select
              id="hr-setup-approve-employee-type"
              label="Employee Type"
              required
              value={f.state.value}
              onChange={(v) => {
                f.handleChange(v);
                if (v !== "REGULAR") {
                  form.setFieldValue("employeeSubType", "");
                }
              }}
              options={employeeTypeOptions
                .filter((option) => ["REGULAR", "CONSULTANT"].includes(String(option.value)))
                .map((option) => ({
                  value: String(option.value),
                  label: option.label,
                }))}
              placeholder="Select Employee Type"
              disabled={busy}
              error={formatFormFieldErrors(f.state.meta.errors)}
            />
          )}
        </form.Field>}

        {isSqe ? (
          <form.Field name={field("employeeSubType")}>
            {(f) => <Select id="hr-setup-approve-employee-sub-type" label="Employee Sub Type" required value={f.state.value ?? ""} onChange={(v) => f.handleChange(v)} options={[{ value: "Full Time", label: "Full Time" }, { value: "Intern", label: "Intern" }]} placeholder="Select Sub Employee Type" disabled={busy} error={formatFormFieldErrors(f.state.meta.errors)} />}
          </form.Field>
        ) : <form.Subscribe selector={(s) => s.values.employeeType}>
          {(employeeType) => employeeType === "REGULAR" ? (
            <form.Field name={field("employeeSubType")}>
              {(f) => (
                <Select
                  id="hr-setup-approve-employee-sub-type"
                  label="Employee Sub Type"
                  required
                  value={f.state.value ?? ""}
                  onChange={(v) => f.handleChange(v)}
                  options={EMPLOYEE_SUB_TYPE_OPTIONS}
                  placeholder="Select Sub Employee Type"
                  disabled={busy}
                  error={formatFormFieldErrors(f.state.meta.errors)}
                />
              )}
            </form.Field>
          ) : null}
        </form.Subscribe>}

        <form.Field name={field("isTech")}>
          {(f) => (
            <Select
              id="hr-setup-approve-is-tech"
              label="Project Eligibility"
              required
              value={String(f.state.value)}
              onChange={(v) => {
                f.handleChange(v === "true");
                if (v !== "true") form.setFieldValue("techStack", "");
              }}
              options={IS_TECH_OPTIONS}
              placeholder="Select"
              disabled={busy}
              error={formatFormFieldErrors(f.state.meta.errors)}
            />
          )}
        </form.Field>

        <form.Subscribe selector={(s) => s.values.isTech}>
          {(isTech) => isTech ? (
            <form.Field name={field("techStack")}>
              {(f) => (
                <Select
                  id="hr-setup-approve-tech-stack"
                  label="Tech Stack"
                  searchable
                  required
                  value={f.state.value}
                  onChange={(v) => f.handleChange(v)}
                  options={techStacks}
                  placeholder="Select Tech Stack"
                  disabled={busy}
                  error={formatFormFieldErrors(f.state.meta.errors)}
                />
              )}
            </form.Field>
          ) : null}
        </form.Subscribe>

        <div className={styles.probationPeriodWrapper}>
          <form.Field name={field("provisionPreset")}>
            {(f) => (
              <Select
                id="hr-setup-approve-provision-preset"
                label="Probation Period"
                required
                value={f.state.value}
                onChange={(v) => f.handleChange(v as HrApproveFormValues["provisionPreset"])}
                options={PROVISION_PRESET_OPTIONS}
                placeholder="Select Provision Period"
                disabled={busy}
                error={formatFormFieldErrors(f.state.meta.errors)}
              />
            )}
          </form.Field>

          <form.Subscribe selector={(s) => s.values.provisionPreset}>
            {(provisionPreset) =>
              provisionPreset === "custom" ? (
                <div className={styles.customProvision}>
                  <form.Field name={field("customProvision")}>
                    {(f) => (
                      <TimePeriodSelect
                        id="hr-setup-approve-custom-provision"
                        value={f.state.value ?? { amount: 1, unit: "month" }}
                        onChange={(v) => f.handleChange(v)}
                        disabled={busy}
                        error={formatFormFieldErrors(f.state.meta.errors)}
                      />
                    )}
                  </form.Field>
                </div>
              ) : null
            }
          </form.Subscribe>
        </div>

        <div className={styles.probationPeriodWrapper}>
          <form.Field name={field("noticePeriodPreset")}>
            {(f) => (
              <Select
                id="hr-setup-approve-notice-period"
                label="Notice Period"
                required
                value={f.state.value}
                onChange={(v) =>
                  f.handleChange(v as HrApproveFormValues["noticePeriodPreset"])
                }
                options={NOTICE_PERIOD_PRESET_OPTIONS}
                placeholder="Select Notice Period"
                disabled={busy}
                error={formatFormFieldErrors(f.state.meta.errors)}
              />
            )}
          </form.Field>

          <form.Subscribe selector={(s) => s.values.noticePeriodPreset}>
            {(noticePeriodPreset) =>
              noticePeriodPreset === "custom" ? (
                <div className={styles.customProvision}>
                  <form.Field name={field("customNoticePeriod")}>
                    {(f) => (
                      <TimePeriodSelect
                        id="hr-setup-approve-custom-notice-period"
                        value={f.state.value ?? { amount: 1, unit: "month" }}
                        onChange={(v) => f.handleChange(v)}
                        disabled={busy}
                        error={formatFormFieldErrors(f.state.meta.errors)}
                      />
                    )}
                  </form.Field>
                </div>
              ) : null
            }
          </form.Subscribe>
        </div>
      </div>

      <div className={styles.actions}>
        <Button type="button" variant="muted-outline" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={busy}>
          {busy ? "Approving…" : "Approve"}
        </Button>
      </div>
    </form>
  );
}
